import type { ComputedRef, Ref } from 'vue';
import type { TButtonProps } from './TButton.types';
import { css } from '@emotion/css';
import { darken, lighten } from 'polished';
import { computed } from 'vue';
import { useTheme } from '../../composables/theme';
import { toCamelCase } from '../../utils';

export const useStyles = (color:
  | Ref<NonNullable<TButtonProps['color']>>
  | ComputedRef<NonNullable<TButtonProps['color']>>) => {
  const theme = useTheme();
  const variant = computed(() => toCamelCase(color.value));
  const ultraDark = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.ultraDark
    : darken(0.15, theme.value.colors[variant.value])));
  const main = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.main
    : theme.value.colors[variant.value]));
  const light1 = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.light1
    : lighten(0.15, theme.value.colors[variant.value])));
  const light2 = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.light2
    : lighten(0.2, theme.value.colors[variant.value])));
  const light3 = computed(() => (variant.value === 'primary'
    ? theme.value.colors.primary.light3
    : lighten(0.25, theme.value.colors[variant.value])));

  const baseClass = computed(() => css({
    '&, *': {
      margin: 0,
      padding: 0,
      boxSizing: 'border-box',
    },
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.625rem',
    fontWeight: theme.value.typography.fontWeight.semibold,
    '&--primary': {
      borderRadius: theme.value.button.borderRadius,
      backgroundColor: main.value,
      boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.1)',
      border: `1px solid ${main.value}`,
      color: theme.value.typography.buttonTextColor,
      '&:hover:not(&:disabled)': {
        backgroundColor: ultraDark.value,
        color: theme.value.typography.buttonTextColor,
      },
      '&:focus': {
        border: `1px solid ${light1.value}`,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        border: `1px solid ${theme.value.colors.gray.light2}`,
        backgroundColor: theme.value.colors.gray.light2,
        color: theme.value.colors.gray.medium2,
      },
    },
    '&--secondary': {
      borderRadius: theme.value.button.borderRadius,
      backgroundColor: '#FFFFFF',
      border: `1px solid ${main.value}`,
      boxShadow: '0px 0px 8px 0px rgba(0, 0, 0, 0.1)',
      color: main.value,
      '&:hover:not(&:disabled)': {
        backgroundColor: light3.value,
      },
      '&:focus': {
        border: `1px solid ${light1.value}`,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        border: `1px solid ${theme.value.colors.gray.light2}`,
        backgroundColor: theme.value.colors.gray.light2,
        color: theme.value.colors.gray.medium2,
      },
    },
    '&--tertiary': {
      background: 'none',
      border: 'none',
      margin: 0,
      padding: 0,
      color: main.value,
      '&:hover:not(&:disabled)': {
        color: light2.value,
      },
      '&:focus': {
        color: light1.value,
      },
      '&:disabled': {
        cursor: 'not-allowed',
        color: theme.value.colors.gray.medium1,
      },
    },
    '&--large': {
      height: '3rem',
      padding: '0.5rem 1rem',
      lineHeight: '1.5rem',
      fontSize: '1.125rem',
    },
    '&--medium': {
      height: '2.5rem',
      padding: '0.5rem 1rem',
      lineHeight: '1.375rem',
      fontSize: '1rem',
    },
    '&--small': {
      height: '2rem',
      padding: '0.5rem 0.75rem',
      lineHeight: '1.25rem',
      fontSize: '0.875rem',
    },
    '&--full-width': {
      width: '100%',
    },
    '&--loading, &--loading:disabled, &--loading:hover:not(&:disabled), &--loading:focus':
        {
          color: 'transparent',
          position: 'relative',
        },
    '&__spinner': {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
    },
    '&--primary &__spinner': {
      color: '#fff',
    },
    '&--loading:disabled &__spinner': {
      color: theme.value.colors.gray.medium1,
    },
    '&--tertiary:disabled &__spinner': {
      color: theme.value.colors.gray.medium1,
    },
    '&--secondary &__spinner, &--tertiary &__spinner': {
      color: main.value,
    },
  }));

  return baseClass;
};
